import { MyBooking, MyBookingStatusUpdate, PagedMyBooking } from "@models";
import { bookingService } from "@services";
import dayjs from "dayjs";
import { makeAutoObservable, runInAction } from "mobx";

export default class BookingStore {
  myBookings = new Map<number, MyBooking>();
  myBookingsTotalCount = 0;
  myBookingsSkipCount = 0;
  myBookingsMaxResultCount = 6;
  myBookingsLoading = false;

  myFutureBookings = new Map<number, MyBooking>();
  myFutureBookingsTotalCount = 0;
  myFutureBookingsSkipCount = 0;
  myFutureBookingsMaxResultCount = 6;
  myFutureBookingsLoading = false;

  myPastBookings = new Map<number, MyBooking>();
  myPastBookingsTotalCount = 0;
  myPastBookingsSkipCount = 0;
  myPastBookingsMaxResultCount = 6;
  myPastBookingsLoading = false;

  myUnassignedBookings = new Map<number, MyBooking>();
  myUnassignedBookingsTotalCount = 0;
  myUnassignedBookingsSkipCount = 0;
  myUnassignedBookingsMaxResultCount = 6;
  myUnassignedBookingsLoading = false;

  myBookingStatusLoading = false;

  nextBooking: MyBooking | undefined = undefined;
  nextBookingLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  private fetchBookings = async (
    request: PagedMyBooking,
    map: Map<number, MyBooking>,
    setTotalCount: (count: number) => void,
    setLoading: (value: boolean) => void,
  ) => {
    setLoading(true);
    try {
      const response = await bookingService.getMyBookings(request);
      runInAction(() => {
        response.items.forEach((booking) => {
          map.set(booking.id, booking);
        });
        setTotalCount(response.totalCount);
      });
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  getMyBookings = (request: PagedMyBooking) =>
    this.fetchBookings(
      request,
      this.myBookings,
      (c) => (this.myBookingsTotalCount = c),
      this.setMyBookingsLoading,
    );

  getMyFutureBookings = (request: PagedMyBooking) =>
    this.fetchBookings(
      request,
      this.myFutureBookings,
      (c) => (this.myFutureBookingsTotalCount = c),
      this.setMyFutureBookingsLoading,
    );

  getMyPastBookings = (request: PagedMyBooking) =>
    this.fetchBookings(
      request,
      this.myPastBookings,
      (c) => (this.myPastBookingsTotalCount = c),
      this.setMyPastBookingsLoading,
    );

  getMyUnassignedBookings = (request: PagedMyBooking) =>
    this.fetchBookings(
      request,
      this.myUnassignedBookings,
      (c) => (this.myUnassignedBookingsTotalCount = c),
      this.setMyUnassignedBookingsLoading,
    );

  updateMyBookingStatus = async (request: MyBookingStatusUpdate) => {
    this.setStatusUpdateLoading(true);
    try {
      await bookingService.updateMyBookingStatus(request);
      this.setMyFutureBookingStatus(request);
      this.setMyUnassignedBookingStatus(request);
      this.setStatusUpdateLoading(false);
    } catch (error) {
      console.error(error);
      this.setStatusUpdateLoading(false);
    }
  };

  setMyFutureBookingStatus = (request: MyBookingStatusUpdate) => {
    const myFutureBooking = this.myFutureBookings.get(request.id);
    if (!myFutureBooking) return;

    myFutureBooking.status = request.status;

    runInAction(() => {
      this.myFutureBookings.set(request.id, myFutureBooking);
    });
  };

  setMyUnassignedBookingStatus = (request: MyBookingStatusUpdate) => {
    const myUnassignedBooking = this.myUnassignedBookings.get(request.id);
    if (!myUnassignedBooking) return;

    runInAction(() => {
      if (dayjs(myUnassignedBooking.start).isAfter(dayjs())) {
        this.myFutureBookings.set(request.id, myUnassignedBooking);
      }

      if (dayjs(myUnassignedBooking.start).isBefore(dayjs())) {
        this.myPastBookings.set(request.id, myUnassignedBooking);
      }

      this.myUnassignedBookings.delete(request.id);
      this.myUnassignedBookingsTotalCount -= 1;
    });
  };

  setMyBookingsLoading = (value: boolean) => {
    runInAction(() => {
      this.myBookingsLoading = value;
    });
  };

  setMyFutureBookingsLoading = (value: boolean) => {
    runInAction(() => {
      this.myFutureBookingsLoading = value;
    });
  };

  setMyPastBookingsLoading = (value: boolean) => {
    runInAction(() => {
      this.myPastBookingsLoading = value;
    });
  };

  setMyUnassignedBookingsLoading = (value: boolean) => {
    runInAction(() => {
      this.myUnassignedBookingsLoading = value;
    });
  };

  clearMyBookings = () => {
    this.myBookings.clear();
  };

  clearMyFutureBookings = () => {
    this.myFutureBookings.clear();
  };

  clearMyPastBookings = () => {
    this.myPastBookings.clear();
  };

  clearMyUnassignedBookings = () => {
    this.myUnassignedBookings.clear();
  };

  setMyBookingsSkipCount = (value: number) => {
    this.myBookingsSkipCount = value;
  };

  setMyFutureBookingsSkipCount = (value: number) => {
    this.myFutureBookingsSkipCount = value;
  };

  setMyPastBookingsSkipCount = (value: number) => {
    this.myPastBookingsSkipCount = value;
  };

  setMyUnassignedBookingsSkipCount = (value: number) => {
    this.myUnassignedBookingsSkipCount = value;
  };

  setStatusUpdateLoading = (value: boolean) => {
    this.myBookingStatusLoading = value;
  };

  getNextBooking = async () => {
    runInAction(() => {
      this.nextBookingLoading = true;
    });
    try {
      const response = await bookingService.getMyBookings({
        future: true,
        date: dayjs().format(),
        skipCount: 0,
        maxResultCount: 1,
      });
      runInAction(() => {
        this.nextBooking = response.items[0];
        this.nextBookingLoading = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => {
        this.nextBookingLoading = false;
      });
    }
  };

  clearNextBooking = () => {
    runInAction(() => {
      this.nextBooking = undefined;
    });
  };
}
